import { Component, ElementRef, EventEmitter, effect, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription, firstValueFrom } from 'rxjs';
import { PostResponseProjectsRequestBody, ResponseProject } from '../../models/response-project';
import { ApiResponseProjectsService } from '../../services/api/response-projects/api-response-projects.service';
import { TendersResponsesService } from '../../services/tenders-responses.service';
import { ResponseModuleState } from '../../utils/response-module.utils';
import { ContactReason, getCsmMailtoHref } from '../../utils/response-mailto.utils';
import { ResponseModuleStateService } from '../../services/response-module-state.service';
import { ResponseBlockedModalService } from '../../services/response-blocked-modal.service';
import { AutoUnsubscribe } from '../../../common-explain/decorators/auto-unsubscribe';

const PROJECTS_LIMIT = 1000;

@AutoUnsubscribe
@Component({
  selector: 'app-response-projects-list',
  templateUrl: './response-projects-list.component.html',
  styleUrls: ['./response-projects-list.component.scss'],
})
export class ResponseProjectsListComponent implements OnInit, OnChanges {
  @Input() activeProjectId: string | null = null;

  @Output() projectSelect = new EventEmitter<string>();
  @Output() projectsLoaded = new EventEmitter<ResponseProject[]>();

  readonly projects = this.tendersResponsesService.projects;

  accountState: ResponseModuleState | null = null;
  activeOpen = true;
  closedOpen = false;
  searchQuery = '';
  creditPopoverOpen = false;

  private accountInfoSub: Subscription | null = null;

  constructor(
    private apiResponseProjects: ApiResponseProjectsService,
    private responseModuleStateService: ResponseModuleStateService,
    private tendersResponsesService: TendersResponsesService,
    private blockedModalService: ResponseBlockedModalService,
    private el: ElementRef<HTMLElement>
  ) {
    effect(() => {
      const projects = this.projects();
      this.projectsLoaded.emit(projects);
      if (this.activeProjectId) {
        const isActiveClosed = projects.some(p => p.id === this.activeProjectId && p.is_active === false);
        this.closedOpen = isActiveClosed;
        this.scheduleScrollToActive();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeProjectId'] && this.activeProjectId) {
      this.scheduleScrollToActive();
    }
  }

  ngOnInit(): void {
    this.loadProjects();

    this.accountInfoSub = this.responseModuleStateService.state$.subscribe(state => {
      this.accountState = state;
    });
  }

  get activeProjects() {
    return this.projects().filter(p => p.id !== 'new' && p.is_active !== false);
  }

  get closedProjects() {
    return this.projects().filter(p => p.is_active === false);
  }

  get filteredActiveProjects() {
    const q = this.searchQuery.toLowerCase().trim();
    return q ? this.activeProjects.filter(p => p.name.toLowerCase().includes(q)) : this.activeProjects;
  }

  get filteredClosedProjects() {
    const q = this.searchQuery.toLowerCase().trim();
    return q ? this.closedProjects.filter(p => p.name.toLowerCase().includes(q)) : this.closedProjects;
  }

  get isSearching(): boolean {
    return this.searchQuery.trim().length > 0;
  }

  get effectiveClosedOpen(): boolean {
    return (this.isSearching && this.filteredClosedProjects.length > 0) || this.closedOpen;
  }

  get newProjectPlaceholder() {
    return this.projects().find(p => p.id === 'new') ?? null;
  }

  get creditsUsed(): number {
    return this.accountState?.nbCreated ?? this.projects().length;
  }

  get creditsAllowed(): number | null {
    return this.accountState?.nbAllowed ?? null;
  }

  get creditsLeft(): number {
    if (this.creditsAllowed === null) return 0;
    return Math.max(this.creditsAllowed - this.creditsUsed, 0);
  }

  get deletedCount(): number {
    return this.creditsUsed - this.activeProjects.length - this.closedProjects.length;
  }

  get isQuotaReached(): boolean {
    return this.creditsAllowed !== null && this.creditsLeft === 0;
  }

  get isBlocked(): boolean {
    return (this.accountState?.isExpired ?? false) || this.isQuotaReached;
  }

  get expirationDate(): string | null {
    return this.accountState?.expirationDate ?? null;
  }

  private loadProjects(): void {
    const body: PostResponseProjectsRequestBody = {
      fields: ['id', 'name', 'is_active'],
      filters: {},
      limit: PROJECTS_LIMIT,
      offset: 0,
    };
    this.apiResponseProjects.searchResponseProjects(body).subscribe(({ data }) => {
      this.tendersResponsesService.setProjects(data);
      if (!this.activeProjectId) {
        const first = this.activeProjects[0] ?? this.closedProjects[0];
        if (first) this.onProjectSelect(first.id);
      }
    });
  }

  toggleClosed(event: MouseEvent): void {
    this.closedOpen = !this.closedOpen;
    if (this.closedOpen) {
      const btn = event.currentTarget as HTMLElement;
      const container = document.querySelector('.response-projects-list-scroll') as HTMLElement;
      if (container) {
        setTimeout(() => {
          container.scrollTo({ top: btn.offsetTop, behavior: 'smooth' });
        }, 50);
      }
    }
  }

  private scheduleScrollToActive(): void {
    setTimeout(() => {
      const scroll = this.el.nativeElement.querySelector('.response-projects-list-scroll') as HTMLElement | null;
      const active = this.el.nativeElement.querySelector('.response-projects-list-item-active') as HTMLElement | null;
      if (scroll && active) {
        const itemTop = active.offsetTop - scroll.offsetTop;
        const itemBottom = itemTop + active.offsetHeight;
        const visible = scroll.scrollTop + scroll.clientHeight;
        if (itemTop < scroll.scrollTop || itemBottom > visible) {
          scroll.scrollTo({ top: itemTop - scroll.clientHeight / 2 + active.offsetHeight / 2, behavior: 'smooth' });
        }
      }
    }, 50);
  }

  onProjectSelect(projectId: string): void {
    const isClosed = this.projects().some(p => p.id === projectId && p.is_active === false);
    this.closedOpen = isClosed;
    this.projectSelect.emit(projectId);
  }

  async createProject(): Promise<void> {
    const wasQuotaReached = this.isQuotaReached;
    const state = await firstValueFrom(this.responseModuleStateService.refresh());
    if (state?.isExpired) {
      this.openBlockedModal('subscription-expired');
      return;
    }
    const nbAllowed = state?.nbAllowed;
    const nbCreated = state?.nbCreated;
    if (nbAllowed != null && nbCreated != null && nbCreated >= nbAllowed) {
      this.openBlockedModal(wasQuotaReached ? 'quota-exceeded' : 'slot-conflict');
      return;
    }
    this.tendersResponsesService.addNewProjectPlaceholder();
    this.projectSelect.emit('new');
  }

  get mailtoHref(): string {
    return getCsmMailtoHref(ContactReason.CreditsOut);
  }

  private openBlockedModal(mode: 'quota-exceeded' | 'subscription-expired' | 'slot-conflict'): void {
    this.blockedModalService.open(mode, null, {
      isTrial: this.accountState?.isTrial ?? false,
      planLabel: this.accountState?.planLabel ?? '',
    });
  }
}
